import { useQuery } from "@tanstack/react-query";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useAppLanguage from "../hooks/useAppLanguage";
import LanguageContext from "./language";
import UserContext from "./user";
import dayjs from "dayjs";
import { translateSurvey } from "../utils/lang";
import sortOnField from "../utils/sortOnField";
import { parseCheckupSubmission, parseRequest } from "../utils/check-up";

const CheckUpContext = createContext({
  status: undefined,
  error: undefined,
  navigation: undefined,
  showDetail: undefined,
  formData: undefined,
  partial: undefined,
  path: undefined,
  currentRequest: undefined,
  requests: undefined,
  isFetching: undefined,
  isRequestFetching: undefined,
  hideMyBooking: undefined,
  setPartial: () => 0,
  setFormData: () => 0,
  currentSectionIndex: undefined,
  slotNotAvailable: undefined,
  survey: undefined,
  labels: undefined,
  appLang: undefined,
  languages: undefined,
  checkUpInfo: undefined,
  setAppLang: () => 0,
  setNavigation: () => 0,
  setShowDetail: () => 0,
  setCurrentSectionIndex: () => 0,
  goToCartDetail: () => 0,
  handleNext: () => 0,
  setPath: () => 0,
  setSlotNotAvailable: () => 0,
  setHideMyBooking: () => 0,
  setCurrentRequest: () => 0,
});

export const CheckUpProvider = ({ children }) => {
  const { callApi, logged, userProfile } = useContext(UserContext);
  const { language, defaultLabels, unloggedLanguage, cmsLanguages } = useContext(LanguageContext);
  const [navigation, setNavigation] = useState();
  const [showDetail, setShowDetail] = useState();
  const [currentSectionIndex, setCurrentSectionIndex] = useState();
  const [formData, setFormData] = useState({});
  const [partial, setPartial] = useState(false);
  const [path, setPath] = useState([]);
  const [requests, setRequests] = useState();
  const [currentRequest, setCurrentRequest] = useState();
  const [status, setStatus] = useState();
  const [slotNotAvailable, setSlotNotAvailable] = useState();
  const [error, setError] = useState();
  const [hideMyBooking, setHideMyBooking] = useState();
  const locationRef = useRef();

  const { isFetching: isNextFetching, data: nextCheckupDate } = useQuery(
    ["next-check-up-date", JSON.stringify(userProfile)],
    () => callApi(`/checkUp/next`),
    {
      enabled: !!userProfile,
      select: (data) => dayjs(Number(data)),
      onSuccess: (nextCheckupDate) => {
        console.log("Next checkup date:", dayjs(Number(nextCheckupDate)).format("DD/MM/YYYY"));
        const today = dayjs();
        if (!!nextCheckupDate && today.isAfter(dayjs(Number(nextCheckupDate)), "minute")) {
          setStatus("ready-to-book");
        } else {
          setStatus("closed");
        }
      },
      onError: (error) => setError(error),
    }
  );

  const { isFetching: isSurveyFetching, data: survey } = useQuery(
    ["checkup-survey", JSON.stringify(userProfile)],
    () => callApi(`/checkUp/survey`),
    {
      enabled: !!userProfile,
      select: ({ definition, ...data }) => {
        return {
          ...data,
          definition: {
            ...definition,
            sections: definition.sections.sort((a, b) =>
              a.position > b.position ? 1 : a.position < b.position ? -1 : 0
            ),
          },
        };
      },
      onError: (error) => setError(error),
    }
  );

  const { isFetching: isRequestFetching } = useQuery(
    ["requests", JSON.stringify(userProfile)],
    () => callApi("/checkUp/request/search?offset=0&limit=100"),
    {
      enabled: logged && !navigation && !!survey,
      select: (data) =>
        data?.list
          ?.sort((a, b) => sortOnField(a, b, (data) => data.creationDate, false))
          ?.map((request) => parseRequest(request)),
      onError: (error) => setError(error),
      onSuccess: (data) => {
        if (
          status === "ready-to-book" &&
          data?.[0] &&
          !["COMPLETED", "CANCELED"].includes(data?.[0]?.status)
        ) {
          setCurrentRequest(data[0]);
          setRequests(data.slice(1));
        } else {
          setRequests(data);
        }
      },
    }
  );

  const isFetching = useMemo(
    () => isNextFetching || isSurveyFetching || isRequestFetching,
    [isNextFetching, isSurveyFetching, isRequestFetching]
  );

  const { appLang, setAppLang, languages, labels } = useAppLanguage(
    defaultLabels,
    !!survey && language,
    survey,
    logged && (error ? error.status !== 403 : true) && status !== "invalid-campaign"
      ? null
      : unloggedLanguage
  );

  const { error: infoError, data: checkUpInfo } = useQuery(
    ["info-checkup", JSON.stringify(userProfile) + "-" + appLang?.value],
    () =>
      callApi(
        `/resource/hr/${window.CHECKUP_INFO_MASTER_ID}?locale=${
          cmsLanguages?.find((lang) => lang.profile === appLang.value).value || "en"
        }`
      ),
    {
      enabled: !!userProfile && !!survey && !!appLang?.value && !!window.CHECKUP_INFO_MASTER_ID,
    }
  );

  const translatedSurvey = useMemo(() => {
    if (survey) {
      return translateSurvey(survey, appLang);
    }
  }, [survey, appLang]);

  const handlePrevious = useCallback(() => {
    setPath((current) => current.slice(0, current.length - 1));
    setCurrentSectionIndex((current) => current - 1);
  }, []);

  const handleNext = useCallback(
    (submissionId, nextIsGoToSection) => {
      if (submissionId) {
        setPartial(submissionId);
      }
      setPath((current) =>
        current.concat(
          nextIsGoToSection ?? translatedSurvey?.definition?.sections[currentSectionIndex + 1]?.id
        )
      );
      setCurrentSectionIndex((current) => current + 1);
    },
    [currentSectionIndex, translatedSurvey]
  );

  useEffect(() => {
    const currentLocation = navigation + "-" + currentSectionIndex + "-" + showDetail;
    if (locationRef.current !== currentLocation) {
      locationRef.current = currentLocation;
      window.scrollTo(0, 0);
    }
  }, [navigation, currentSectionIndex, showDetail]);

  const getPathFromRequest = useCallback(
    (request) =>
      survey?.definition?.sections
        ?.map((section) => section.id)
        .slice(0, request.lastCompletedStep + 2 || 1),
    [survey]
  );

  useEffect(() => {
    if (!!currentRequest) {
      if (currentRequest === "new") {
        setCurrentSectionIndex(0);
        setPath([survey.definition.sections[0].id]);
      } else {
        if (currentRequest.fromMigration) {
          setCurrentSectionIndex(3);
          setPath(getPathFromRequest({ ...currentRequest, lastCompletedStep: 2 }));
        } else {
          setFormData(parseCheckupSubmission(survey, currentRequest.surveySubmission));
          setCurrentSectionIndex(currentRequest.lastCompletedStep + 1 || 0);
          setPath(getPathFromRequest(currentRequest));
        }
      }
    } else {
      setFormData();
      setCurrentSectionIndex(0);
      setPath([]);
    }
  }, [currentRequest, survey, getPathFromRequest]);

  useEffect(() => {
    if (!logged) {
      setError(null);
    }
  }, [logged]);

  const contextValue = useMemo(
    () => ({
      //campaign,
      status,
      error,
      navigation,
      formData,
      partial,
      setPartial,
      setFormData,
      showDetail,
      currentSectionIndex,
      slotNotAvailable,
      survey: translatedSurvey,
      appLang,
      labels,
      hideMyBooking,
      checkUpInfo,
      currentRequest,
      requests,
      isFetching,
      isRequestFetching,
      infoError,
      setAppLang,
      languages,
      path,
      nextCheckupDate,
      setNavigation,
      setShowDetail,
      setCurrentSectionIndex,
      setRequests,
      handleNext,
      setSlotNotAvailable,
      setHideMyBooking,
      setCurrentRequest,
      setPath,
      handlePrevious,
    }),
    [
      //campaign,
      status,
      error,
      navigation,
      formData,
      partial,
      hideMyBooking,
      checkUpInfo,
      currentRequest,
      requests,
      isFetching,
      isRequestFetching,
      infoError,
      setPartial,
      setFormData,
      showDetail,
      currentSectionIndex,
      slotNotAvailable,
      translatedSurvey,
      appLang,
      labels,
      setAppLang,
      languages,
      path,
      nextCheckupDate,
      setNavigation,
      setShowDetail,
      setCurrentSectionIndex,
      setRequests,
      handleNext,
      setSlotNotAvailable,
      setHideMyBooking,
      setCurrentRequest,
      setPath,
      handlePrevious,
    ]
  );

  return <CheckUpContext.Provider value={contextValue}>{children}</CheckUpContext.Provider>;
};

export default CheckUpContext;
